import {
  Button,
  Col,
  Form,
  Layout,
  message,
  Row,
  Tabs,
  Table,
  Menu,
  Dropdown,
} from "antd";
import { ChevronDown, Eye, Pen } from "lucide-react";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatNumber,
  formatString,
  navigateTo,
  getUserRole
} from "../../Utils/Common";
import { hasRole } from "../../Utils/AccessControl";
import { postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useTableData } from "../../Utils/useTableData";

const { TabPane } = Tabs;

const EndOfDay = () => {
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const [form] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [activeTab, setActiveTab] = useState("for_deposit");

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    refetch: fetchTableData,
    handleChangePage,
    handlePageSizeChange,
  } = useTableData<any>({
    endpoint: "eods/get_detail",
    initialFilters: { status: "for_deposit" },
    isFormData: true,
  });

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
  }

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({ ...prev, status: activeKey, page: 1 }));
  }

  function actionColumn(path: string, label: string) {
    return {
      title: "",
      key: "action",
      width: 10,
      render: (_: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<Eye strokeWidth={1.5} size={15} />}
                onClick={() => navigateTo(path + record.id)}
              >
                {label}
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
        </Dropdown>
      ),
    };
  }

  function getTableColumns() {
    const baseColumns = [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        render: (date: any) => formatDate(date, "MMMM DD, YYYY"),
      },
      {
        title: "Cashier",
        dataIndex: "cashier_name",
        key: "cashier_name",
      },
      {
        title: "Total Sales RWT",
        dataIndex: "total_daily_transaction",
        key: "total_daily_transaction",
        align: "right" as any,
        render: (value: any) => formatNumber(value),
      },
      {
        title: "System Cash Payment",
        dataIndex: "system_cash_sales",
        key: "system_cash_sales",
        align: "right" as any,
        render: (value: any) => formatNumber(value),
      },
      {
        title: "Cash Count",
        key: "cash_out",
        align: "right" as any,
        render: (_: any, record: any) =>
          formatNumber((record.system_cash_sales || 0) - (record.total_borrowed_cash || 0)),
      },
      {
        title: "Borrowed Cash",
        dataIndex: "total_borrowed_cash",
        key: "total_borrowed_cash",
        align: "right" as any,
        render: (value: any) => formatNumber(value),
      },
      {
        title: "Cash Variance",
        dataIndex: "cash_variance",
        key: "cash_variance",
        align: "right" as any,
        render: (value: any) => formatNumber(value),
      },
      {
        title: "Check Payment",
        dataIndex: "total_check_billing",
        key: "total_check_billing",
        align: "right" as any,
        render: (value: any) => formatNumber(value),
      },
      {
        title: "Bank Transfer Payments",
        dataIndex: "total_transfer_billing",
        key: "total_transfer_billing",
        align: "right" as any,
        render: (value: any) => formatNumber(value),
      },
    ];

    const checkedByColumns = [
      {
        title: "Checked By",
        dataIndex: "checked_by_name",
        key: "checked_by_name",
      },
      {
        title: "Checked On",
        dataIndex: "checked_on",
        key: "checked_on",
        align: "right" as any,
        render: (date: any) => formatDate(date, "MMMM DD, YYYY"),
      },
    ];

    const role = getUserRole();
    const isAdminOrCashier = hasRole(role, "admin", "cashier");

    switch (activeTab) {
      case "for_deposit": {
        return [
          ...baseColumns,
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    {!hasRole(role, "cashier") && (
                      <Menu.Item
                        key="view"
                        icon={<Eye strokeWidth={1.5} size={15} />}
                        onClick={() => navigateTo("/end-of-day/view/" + record.id)}
                      >
                        View
                      </Menu.Item>
                    )}
                    {hasRole(role, "cashier") && (
                      <Menu.Item
                        key="review"
                        icon={<Pen strokeWidth={1.5} size={15} />}
                        onClick={() => navigateTo("/end-of-day/edit/" + record.id)}
                      >
                        Upload
                      </Menu.Item>
                    )}
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          },
        ];
      }

      case "for_checking": {
        return [
          ...baseColumns,
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    {!hasRole(role, "accounting") && (
                      <Menu.Item
                        key="view"
                        icon={<Eye strokeWidth={1.5} size={15} />}
                        onClick={() => navigateTo("/end-of-day/view/" + record.id)}
                      >
                        View
                      </Menu.Item>
                    )}
                    {hasRole(role, "accounting") && (
                      <Menu.Item
                        key="review"
                        icon={<Pen strokeWidth={1.5} size={15} />}
                        onClick={() => navigateTo("/end-of-day/check/" + record.id)}
                      >
                        Check
                      </Menu.Item>
                    )}
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          },
        ];
      }

      case "for_approval": {
        return [
          ...baseColumns,
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    {!hasRole(role, "admin") && (
                      <Menu.Item
                        key="view"
                        icon={<Eye strokeWidth={1.5} size={15} />}
                        onClick={() => navigateTo("/end-of-day/view/" + record.id)}
                      >
                        View
                      </Menu.Item>
                    )}
                    {hasRole(role, "admin") && (
                      <Menu.Item
                        key="review"
                        icon={<Pen strokeWidth={1.5} size={15} />}
                        onClick={() => navigateTo("/end-of-day/approval/" + record.id)}
                      >
                        Approve
                      </Menu.Item>
                    )}
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          },
        ];
      }

      case "approved": {
        return [
          ...baseColumns,
          ...checkedByColumns,
          {
            title: "Approved By",
            dataIndex: "reviewed_by_name",
            key: "reviewed_by_name",
          },
          {
            title: "Approved On",
            dataIndex: "reviewed_on",
            key: "reviewed_on",
            align: "right" as any,
            render: (date: any) => formatDate(date, "MMMM DD, YYYY"),
          },
          actionColumn("/end-of-day/view/", "View"),
        ];
      }

      case "disapproved": {
        return [
          ...baseColumns,
          ...checkedByColumns,
          {
            title: "Disapproved By",
            dataIndex: "reviewed_by_name",
            key: "reviewed_by_name",
          },
          {
            title: "Disapproved On",
            dataIndex: "reviewed_on",
            key: "reviewed_on",
            align: "right" as any,
            render: (date: any) => formatDate(date, "MMMM DD, YYYY"),
          },
          actionColumn("/end-of-day/view/", "View"),
        ];
      }

      default:
        return baseColumns;
    }
  }

  async function submitForm(data: any) {
    setLoading("submit");
    var response = await postRequest(
      `expense-types/${showModal}`,
      { expense_type_id: selectedRow.id, ...data },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        fetchTableData();
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} expense type.`
      );
    }
  }

  async function deleteEndOfDay() {
    var response = await postRequest(
      `expense-types/delete`,
      {
        expense_type_id: selectedRow.id,
      },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        fetchTableData();
      }, 1000);
    } else {
      message.error(
        formatString(
          response?.data?.response?.data?.messages?.error_messages[0] ??
            "Failed to delete expense type."
        )
      );
    }
  }

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="End of Day"
              withDateRangeFilter={false}
              withAddButton={hasRole(getUserRole(), "cashier")}
              addBtnText="End of Day"
              onCreate={() => navigateTo("/end-of-day/add")}
              withSearchBtn={false}
            />
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <Tabs
                  type="card"
                  className="custom-tabs"
                  activeKey={activeTab}
                  onTabClick={onChangeTab}
                >
                  <TabPane tab="For Deposit" key="for_deposit">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                    />
                  </TabPane>
                  <TabPane tab="For Checking" key="for_checking">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                    />
                  </TabPane>
                  <TabPane tab="For Approval" key="for_approval">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                    />
                  </TabPane>
                  <TabPane tab="Approved" key="approved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                    />
                  </TabPane>
                  <TabPane tab="Disapproved" key="disapproved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                    />
                  </TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default EndOfDay;